import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Laptop,
  ShoppingBag,
  Smartphone,
  Palette,
  Layers,
  Search,
  ArrowRight
} from "lucide-react";
import "./Home.css";

const services = [
  {
    id: "web-development",
    title: "Website Development",
    desc: "Custom high-performance websites tailored to represent your brand, drive lead generation, and offer smooth UX.",
    icon: Laptop,
    badge: "Web Core"
  },
  {
    id: "shopify-development",
    title: "Shopify Store Development",
    desc: "Turnkey high-converting e-commerce storefronts, custom apps, and automated systems built directly on Shopify.",
    icon: ShoppingBag,
    badge: "E-Commerce"
  },
  {
    id: "mobile-development",
    title: "Mobile App Development",
    desc: "Native iOS & Android apps and cross-platform solutions crafted for seamless performance and user engagement.",
    icon: Smartphone,
    badge: "Mobile"
  },
  {
    id: "ui-ux-design",
    title: "UI/UX Design",
    desc: "Human-centered wireframing, high-fidelity prototypes, user research, and comprehensive design systems.",
    icon: Palette,
    badge: "Creative"
  },
  {
    id: "custom-applications",
    title: "Custom Web Application Development",
    desc: "Scalable SaaS dashboards, internal CRM platforms, business automation hubs, and enterprise software.",
    icon: Layers,
    badge: "Enterprise"
  },
  {
    id: "websiteseo",
    title: "Website SEO",
    desc: "Search-friendly websites optimized for better visibility, technical performance, and sustainable organic growth.",
    icon: Search,
    badge: "SEO"
  }
];

const approachNodes = [
  {
    id: "customer",
    label: "Customer",
    note: "Who the product is for, and what they need to get done."
  },
  {
    id: "workflow",
    label: "Workflow",
    note: "How the work actually moves through the business today."
  },
  {
    id: "content",
    label: "Content",
    note: "The information the experience has to carry, clearly."
  },
  {
    id: "technology",
    label: "Technology",
    note: "What belongs underneath, chosen after the problem is clear."
  },
  {
    id: "goals",
    label: "Goals",
    note: "What the business needs the product to support."
  }
];

const builds = [
  {
    id: "web",
    label: "Web",
    area: "home-cap-web",
    items: ["Business Websites", "WordPress", "Custom Web Experiences"]
  },
  {
    id: "commerce",
    label: "Commerce",
    area: "home-cap-commerce",
    items: ["Shopify", "E-Commerce", "Store Experiences"]
  },
  {
    id: "mobile",
    label: "Mobile",
    area: "home-cap-mobile",
    items: ["iOS", "Android", "Cross-Platform"]
  },
  {
    id: "applications",
    label: "Applications",
    area: "home-cap-applications",
    items: ["SaaS", "Dashboards", "Business Tools", "Custom Applications"]
  },
  {
    id: "uiux",
    label: "UI/UX",
    area: "home-cap-uiux",
    items: ["Product Interfaces", "Design Systems", "Prototyping"]
  }
];

const reasons = [
  {
    num: "01",
    title: "Purpose Over Decoration",
    desc: "Design should make the experience clearer, not simply make it look impressive."
  },
  {
    num: "02",
    title: "Built Around the Business",
    desc: "Technology should support the way a business operates rather than forcing it into a predefined system."
  },
  {
    num: "03",
    title: "Clarity Matters",
    desc: "Clear interfaces, clear content, and clear architecture make digital products easier to use and easier to maintain."
  },
  {
    num: "04",
    title: "Designed to Evolve",
    desc: "Good digital products should have room to grow as the business and its needs change."
  }
];

const processSteps = [
  {
    step: 1,
    name: "Discover",
    title: "Understand the business before the build.",
    desc: "Understand the business, audience, requirements, and goals.",
    status: "In progress",
    tone: "progress"
  },
  {
    step: 2,
    name: "Plan",
    title: "Set the structure before the screens.",
    desc: "Define the structure, scope, priorities, and technical direction.",
    status: "Drafting",
    tone: "review"
  },
  {
    step: 3,
    name: "Design",
    title: "Shape the experience people will use.",
    desc: "Create the experience, interface, and interaction system.",
    status: "In review",
    tone: "review"
  },
  {
    step: 4,
    name: "Build",
    title: "Develop the product itself.",
    desc: "Develop the actual website, application, commerce experience, or digital product.",
    status: "Building",
    tone: "progress"
  },
  {
    step: 5,
    name: "Test",
    title: "Check the work before it ships.",
    desc: "Review usability, responsiveness, functionality, and technical quality.",
    status: "Testing",
    tone: "review"
  },
  {
    step: 6,
    name: "Launch",
    title: "Prepare it for the real environment.",
    desc: "Prepare the product for its real environment.",
    status: "Ready",
    tone: "ready"
  },
  {
    step: 7,
    name: "Improve",
    title: "Keep refining after launch.",
    desc: "Continue refining the experience as needs evolve.",
    status: "Ongoing",
    tone: "live"
  }
];

function WorkPill({ children, tone = "open" }) {
  return <em className={`home-pill is-${tone}`}>{children}</em>;
}

function WorkMark({ state }) {
  if (state === "done") {
    return (
      <span className="home-mark is-done" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="12" height="12">
          <path d="M3.2 8.3 6.3 11.2 12.8 4.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  return <span className={`home-mark is-${state}`} aria-hidden="true" />;
}

function StageWorkspace({ step }) {
  if (step.step === 1) {
    const rows = [
      ["Business goals", "Reviewed", "done", "ready"],
      ["Audience", "Reviewed", "done", "ready"],
      ["Requirements", "Noted", "done", "progress"],
      ["Technical needs", "Open", "open", "open"]
    ];
    return (
      <div className="home-work is-split">
        <article className="home-work-card">
          <header className="home-work-card-head">
            <strong>Project discovery</strong>
            <span>Brief</span>
          </header>
          <ul className="home-work-list">
            {rows.map(([label, state, mark, tone]) => (
              <li key={label}>
                <WorkMark state={mark} />
                <strong>{label}</strong>
                <WorkPill tone={tone}>{state}</WorkPill>
              </li>
            ))}
          </ul>
        </article>
        <article className="home-work-card">
          <header className="home-work-card-head">
            <strong>Business</strong>
            <span>Notes</span>
          </header>
          <ul className="home-notes">
            <li><strong>Goals</strong><span>What the project needs to achieve.</span></li>
            <li><strong>Audience</strong><span>Who the product is for.</span></li>
            <li><strong>Constraints</strong><span>Limits the build has to respect.</span></li>
          </ul>
        </article>
      </div>
    );
  }

  if (step.step === 2) {
    const cards = [
      ["Scope", "Project boundaries", "Defined"],
      ["Sitemap", "Page structure", "Mapped"],
      ["Direction", "Technical path", "Set"]
    ];
    const sequence = ["Conversation", "Scope", "Build", "Launch"];
    const priorities = [
      ["High", "Core experience", "ready"],
      ["Next", "Content structure", "progress"],
      ["Later", "Future features", "open"]
    ];
    return (
      <div className="home-work">
        <div className="home-work-board">
          {cards.map(([label, title, state]) => (
            <article key={label} className="home-work-card">
              <header className="home-work-card-head">
                <strong>{label}</strong>
              </header>
              <p>{title}</p>
              <WorkPill tone="progress">{state}</WorkPill>
            </article>
          ))}
        </div>
        <div className="home-work-card home-work-sequence">
          <header className="home-work-card-head">
            <strong>Sequence</strong>
            <span>Planning</span>
          </header>
          <ol>
            {sequence.map((item, index) => (
              <li key={item}>
                {index > 0 ? <span className="home-seq-arrow" aria-hidden="true">→</span> : null}
                <strong className={item === "Scope" ? "is-on" : ""}>{item}</strong>
              </li>
            ))}
          </ol>
        </div>
        <ul className="home-work-list home-priorities">
          {priorities.map(([level, label, tone]) => (
            <li key={level}>
              <WorkPill tone={tone}>{level}</WorkPill>
              <strong>{label}</strong>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (step.step === 3) {
    return (
      <div className="home-work is-split">
        <article className="home-preview" aria-label="Interface preview">
          <div className="home-preview-chrome">
            <span />
            <span />
            <span />
            <small>Preview</small>
          </div>
          <div className="home-preview-page">
            <div className="home-preview-nav">
              <b><i />Site</b>
              <span>Home</span>
              <span>Services</span>
              <em>Contact</em>
            </div>
            <div className="home-preview-hero">
              <strong>Clear interface</strong>
              <span>Layout, type, and the next action.</span>
              <em>Primary</em>
            </div>
            <div className="home-preview-cards">
              <p><small>Content</small><strong>Section</strong></p>
              <p><small>Action</small><strong>Next step</strong></p>
            </div>
          </div>
        </article>
        <div className="home-work-stack">
          <article className="home-work-card">
            <header className="home-work-card-head">
              <strong>Components</strong>
            </header>
            <div className="home-comp-row">
              <span className="home-comp-btn">Button</span>
              <span className="home-comp-card">Card</span>
              <span className="home-comp-field">Field</span>
            </div>
          </article>
          <article className="home-work-card">
            <header className="home-work-card-head">
              <strong>Direction</strong>
            </header>
            <div className="home-swatches">
              <span><i className="is-navy" />Navy</span>
              <span><i className="is-blue" />Blue</span>
              <span><i className="is-cyan" />Cyan</span>
            </div>
            <div className="home-viewports">
              <span className="is-on">Desktop</span>
              <span>Tablet</span>
              <span>Mobile</span>
            </div>
          </article>
        </div>
      </div>
    );
  }

  if (step.step === 4) {
    const rows = [
      ["Frontend", "In progress", "progress", 2],
      ["Backend", "In progress", "progress", 2],
      ["Integrations", "Queued", "open", 1],
      ["Content", "Ready", "ready", 3]
    ];
    return (
      <div className="home-work">
        <ul className="home-work-list home-build-list">
          {rows.map(([label, state, tone, level]) => (
            <li key={label}>
              <strong>{label}</strong>
              <span className="home-segs" data-level={level} aria-hidden="true"><i /><i /><i /></span>
              <WorkPill tone={tone}>{state}</WorkPill>
            </li>
          ))}
        </ul>
        <div className="home-work-tags">
          <span>Interface</span>
          <span>API</span>
          <span>Content</span>
          <span>Integrations</span>
        </div>
      </div>
    );
  }

  if (step.step === 5) {
    const rows = [
      ["Responsive", "Pass", "done", "ready"],
      ["Browsers", "Pass", "done", "ready"],
      ["Forms", "In review", "progress", "review"],
      ["Performance", "In review", "progress", "review"],
      ["Accessibility", "In review", "progress", "review"]
    ];
    return (
      <div className="home-work">
        <ul className="home-work-list">
          {rows.map(([label, state, mark, tone]) => (
            <li key={label}>
              <WorkMark state={mark} />
              <strong>{label}</strong>
              <WorkPill tone={tone}>{state}</WorkPill>
            </li>
          ))}
        </ul>
        <div className="home-work-card">
          <header className="home-work-card-head">
            <strong>Coverage</strong>
            <span>Open checks</span>
          </header>
          <div className="home-work-tags">
            <span>Desktop</span>
            <span>Tablet</span>
            <span>Phone</span>
            <span>Chrome</span>
            <span>Safari</span>
            <span>Firefox</span>
          </div>
        </div>
      </div>
    );
  }

  if (step.step === 6) {
    const rows = [
      ["Domain", "Prepared", "ready"],
      ["Deployment", "Staged", "progress"],
      ["SEO", "Reviewed", "ready"],
      ["Analytics", "Ready", "ready"]
    ];
    return (
      <div className="home-work">
        <article className="home-work-card home-launch-flag">
          <WorkMark state="done" />
          <div>
            <header className="home-work-card-head">
              <strong>Production</strong>
              <WorkPill tone="ready">Ready</WorkPill>
            </header>
            <p>Release checklist for the live environment.</p>
          </div>
        </article>
        <ul className="home-work-list">
          {rows.map(([label, state, tone]) => (
            <li key={label}>
              <WorkMark state={tone === "ready" ? "done" : "progress"} />
              <strong>{label}</strong>
              <WorkPill tone={tone}>{state}</WorkPill>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const queue = [
    ["Performance review", "Open", "open"],
    ["Content updates", "Queued", "progress"],
    ["Analytics review", "Next", "review"],
    ["Feature requests", "Later", "open"],
    ["Ongoing support", "Active", "live"]
  ];
  return (
    <div className="home-work">
      <article className="home-work-card home-focus">
        <header className="home-work-card-head">
          <strong>This cycle</strong>
          <WorkPill tone="open">Open</WorkPill>
        </header>
        <p>Performance review is the current improvement item.</p>
      </article>
      <ol className="home-queue">
        {queue.map(([label, state, tone], index) => (
          <li key={label}>
            <span>0{index + 1}</span>
            <strong>{label}</strong>
            <WorkPill tone={tone}>{state}</WorkPill>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ProcessPanel({ step }) {
  return (
    <div className="home-step-panel" id="home-process-panel" data-testid={`process-step-panel-${step.step}`}>
      <div className="home-work-top">
        <p>0{step.step} · {step.name}</p>
        <WorkPill tone={step.tone}>{step.status}</WorkPill>
      </div>
      <h3>{step.title}</h3>
      <p className="home-step-desc">{step.desc}</p>
      <StageWorkspace key={step.step} step={step} />
    </div>
  );
}

export default function Home() {
  useEffect(() => {
    const previous = document.title;
    document.title = "BizLogic Developers";
    return () => { document.title = previous; };
  }, []);

  const [approachId, setApproachId] = useState(approachNodes[0].id);
  const [buildId, setBuildId] = useState(builds[0].id);
  const [activeProcessStep, setActiveProcessStep] = useState(1);

  const approach = approachNodes.find((node) => node.id === approachId) || approachNodes[0];
  const build = builds.find((item) => item.id === buildId) || builds[0];
  const activeStep = processSteps.find((step) => step.step === activeProcessStep) || processSteps[0];

  return (
    <div data-testid="home-page" className="home-page">
      <section className="home-hero">
        <div className="home-hero-glow" aria-hidden="true" />
        <div className="home-wrap home-hero-grid">
          <div className="home-hero-copy">
            <p className="home-eyebrow">Software for modern businesses</p>
            <h1>
              Building Powerful <span>Digital Experiences</span> for Modern Businesses
            </h1>
            <p className="home-hero-lead">
              We help startups, small businesses, and enterprises grow through custom websites, Shopify stores, mobile applications, and innovative digital solutions.
            </p>
            <div className="home-hero-actions">
              <Link
                to="/contact"
                data-testid="hero-consultation-btn"
                className="home-btn home-btn-primary"
              >
                Get Free Consultation
              </Link>
              <Link
                to="/services"
                data-testid="hero-work-btn"
                className="home-btn home-btn-secondary"
              >
                Explore Services
                <ArrowRight />
              </Link>
            </div>
          </div>

          <div className="home-hero-visual">
            <article className="home-screen" aria-label="Digital system preview">
              <div className="home-screen-bar">
                <i /><i /><i />
                <span>Digital system</span>
              </div>
              <div className="home-screen-body">
                <div className="home-screen-main">
                  <strong>Around the business</strong>
                  <div className="home-screen-blocks" aria-hidden="true">
                    <article className="home-metric home-metric-overview">
                      <span>Overview</span>
                      <div className="home-metric-chart">
                        <span /><span /><span /><span /><span />
                      </div>
                    </article>
                    <article className="home-metric home-metric-growth">
                      <span>Growth</span>
                      <strong>+24%</strong>
                      <svg className="home-ring" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="14" />
                        <circle cx="18" cy="18" r="14" />
                      </svg>
                    </article>
                    <article className="home-metric home-metric-orders">
                      <span>Orders</span>
                      <strong>128</strong>
                      <div className="home-bars"><i /><i /><i /><i /></div>
                    </article>
                  </div>
                  <ul>
                    <li className="home-screen-row"><b>Website</b><span>Live</span></li>
                    <li className="home-screen-row"><b>Store</b><span>Ready</span></li>
                    <li className="home-screen-row"><b>Product</b><span>Open</span></li>
                  </ul>
                </div>
              </div>
            </article>
            <aside className="home-float home-float-note">
              <p>Digital Systems</p>
              <strong>Built Around Your Business</strong>
            </aside>
            <aside className="home-float home-float-chips">
              <div className="home-chips">
                <span>Web</span>
                <span>Commerce</span>
                <span>Applications</span>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="home-section home-services" id="services-section">
        <div className="home-wrap">
          <div className="home-services-head">
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 font-heading">Our Core Services</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-heading tracking-tight">
              High-Converting Digital Products Crafted with Precision
            </h2>
            <div className="w-16 h-1.5 bg-blue-600 rounded-full" />
          </div>

          <div className="home-services-grid">
            {services.map((srv, idx) => {
              const IconComp = srv.icon;
              return (
                <Link
                  key={srv.id}
                  to={`/services/${srv.id}`}
                  data-testid={`service-card-link-${idx}`}
                  className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group flex flex-col items-start"
                >
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 group-hover:bg-blue-600 flex items-center justify-center mb-6 transition-colors duration-300">
                    <IconComp className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-cyan-500 mb-2 font-heading">
                    {srv.badge}
                  </span>
                  <h3 className="text-xl font-bold text-slate-950 mb-4 font-heading group-hover:text-blue-600 transition-colors">
                    {srv.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {srv.desc}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:text-blue-700 transition-colors">
                    Explore Service Detail
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="home-section home-approach" aria-labelledby="home-approach-title">
        <div className="home-wrap home-split">
          <div>
            <p className="home-eyebrow">The BizLogic Approach</p>
            <h2 id="home-approach-title" className="home-title">
              We Start With the Problem. Not the Technology.
            </h2>
            <p className="home-lead">
              We understand the business, the people using it, and the workflow behind it before deciding what technology belongs underneath.
            </p>
          </div>

          <div className="home-board">
            <ul className="home-inputs">
              {approachNodes.map((node) => (
                <li key={node.id}>
                  <button
                    type="button"
                    className={`home-input${approachId === node.id ? " is-on" : ""}`}
                    onClick={() => setApproachId(node.id)}
                    onMouseEnter={() => setApproachId(node.id)}
                    aria-pressed={approachId === node.id}
                  >
                    <i />
                    <span>{node.label}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="home-v" aria-hidden="true" />
            <article className="home-core">
              <small>Business Need</small>
              <strong>{approach.label}</strong>
              <p>{approach.note}</p>
            </article>
            <div className="home-rail" aria-hidden="true" />
            <div className="home-v" aria-hidden="true" />
            <div className="home-flow" aria-hidden="true" />
            <article className="home-result">
              <small>Outcome</small>
              <strong>Digital Experience</strong>
              <p>The inputs become one product, shaped around how the business works.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="home-section" id="portfolio-section" aria-labelledby="home-build-title">
        <div className="home-wrap">
          <p className="home-eyebrow">What We Build</p>
          <h2 id="home-build-title" className="home-title">
            Digital Products Built Around Real Business Needs.
          </h2>
          <p className="home-lead">
            From websites and commerce platforms to mobile products and custom applications, we build connected digital experiences around the way your business works.
          </p>

          <div className="home-system">
            <article className="home-core-system">
              <p>BizLogic Digital System</p>
              <h3>{build.label}</h3>
              <ul>
                {build.items.map((item) => (
                  <li key={item}>
                    {item}
                    <span>In scope</span>
                  </li>
                ))}
              </ul>
            </article>
            {builds.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`home-cap ${item.area}${buildId === item.id ? " is-on" : ""}`}
                onClick={() => setBuildId(item.id)}
                onMouseEnter={() => setBuildId(item.id)}
                aria-pressed={buildId === item.id}
              >
                <b>{item.label}</b>
                <ul>
                  {item.items.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-why" aria-labelledby="home-why-title">
        <div className="home-wrap">
          <p className="home-eyebrow">Why BizLogic</p>
          <h2 id="home-why-title" className="home-title">
            Built Around Your Business, Not a Template.
          </h2>
          <p className="home-lead">
            Every project starts with understanding the problem, the users, and the workflow. Technology comes after the direction is clear.
          </p>
          <div className="home-why-grid">
            {reasons.map((reason) => (
              <article key={reason.num} className="home-why-card">
                <span>{reason.num}</span>
                <h3>{reason.title}</h3>
                <p>{reason.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-process" aria-labelledby="home-process-title">
        <div className="home-process-grid" aria-hidden="true" />
        <div className="home-wrap" style={{ position: "relative" }}>
          <p className="home-eyebrow is-light">How We Work</p>
          <h2 id="home-process-title" className="home-title">
            A Clear Process From First Conversation to Launch.
          </h2>
          <p className="home-lead">
            The work moves from the first conversation through launch, and into what happens after.
          </p>

          <div className="home-process-layout">
            <nav className="home-process-nav" aria-label="Project stages">
              {processSteps.map((step) => {
                const selected = step.step === activeProcessStep;
                const done = step.step < activeProcessStep;
                return (
                  <div key={step.step} className={`home-step${selected ? " is-on" : ""}${done ? " is-done" : ""}`}>
                    <button
                      type="button"
                      className="home-step-btn"
                      onClick={() => setActiveProcessStep(step.step)}
                      data-testid={`process-step-trigger-${step.step}`}
                      aria-pressed={selected}
                      aria-controls="home-process-panel"
                    >
                      <em>0{step.step}</em>
                      <span>
                        <strong>{step.name}</strong>
                        <small>Stage {step.step}</small>
                      </span>
                    </button>
                  </div>
                );
              })}
            </nav>
            <ProcessPanel step={activeStep} />
          </div>
        </div>
      </section>

      <section className="home-cta" aria-labelledby="home-cta-title">
        <div className="home-cta-grid" aria-hidden="true" />
        <div className="home-cta-glow" aria-hidden="true" />
        <div className="home-wrap home-cta-inner">
          <p className="home-eyebrow is-light">Start a Project</p>
          <h2 id="home-cta-title">Let&apos;s Build Something That Works.</h2>
          <p className="home-cta-lead">
            Tell us what you&apos;re trying to build, improve, or solve. We&apos;ll help shape the right digital approach for your business.
          </p>
          <div className="home-cta-actions">
            <Link to="/contact" data-testid="final-cta-btn" className="home-btn home-btn-primary">
              Start a Project
              <ArrowRight />
            </Link>
            <Link to="/services" className="home-btn home-btn-ghost">
              Explore Services
              <ArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
